import {
  ArrowTopRightOnSquare,
  AutoMode,
  ChevronRight,
  ClockOutline,
  Close,
  Data,
  DsrActive,
  Fire,
  Geolocation,
  Holiday,
  HotWater,
  Info,
  Menu,
  Passkey,
  PlayCircle,
  PlusHours,
  QuestionMarkCircle,
  Scale,
  Schedule,
  Settings,
  Snowflake,
  Thermometer,
  Thermostat,
  Tick,
} from '../../../app/src/components/common/Icons.js';

// The web's icon set: the mobile app's own glyphs (components/common/Icons.js),
// drawn in place through the react-native-svg shim (plugins/appShims.js), each
// sized to the app's 24px and tinted with currentColor so the container's colour
// applies — tokens, hover and disabled states — as the web's earlier hand copies
// did. Nothing is redrawn here, so a change to an app icon reaches the web at the
// next build; the copies this file used to hold, and the re-sync they needed, are
// gone.
//
// TINT. The app's glyphs take colour three different ways — a `fill` prop, a
// `stroke` prop, or a colour written straight onto the path — so "pass
// currentColor to the right prop" is not one rule. Instead every glyph sits in a
// span.app-icon and a stylesheet rule (index.css, .app-icon--tint) retints
// whatever it draws with currentColor: a presentation attribute loses to a
// stylesheet, so the app's hard-coded whites, greys and greens give way, while
// fill="none" / stroke="none" are left alone. A glyph the app draws in two colours
// on purpose (Geolocation) opts out and keeps the app's own.
//
// Two are not the app's: SpinnerIcon (web-only) and MapIcon (a heroicons glyph the
// app takes from react-native-heroicons, which the web does not depend on).
//
// They are decorative — every caller marks the wrapper aria-hidden and supplies a
// text label — so they carry no title/role of their own.

// One app glyph, sized and tinted for the web. The app's 24 is passed as width and
// height (a few glyphs leave sizing to their container). `id` is cleared: a glyph
// exported from a design tool can carry a fixed element id, which would repeat if
// it were drawn twice on a page.
function AppIcon({ glyph: Glyph, tint = true }) {
  return (
    <span className={tint ? 'app-icon app-icon--tint' : 'app-icon'}>
      <Glyph width={24} height={24} id={undefined} focusable="false" />
    </span>
  );
}

// Heating zone — the thermostat dial.
export function ThermostatIcon() {
  return <AppIcon glyph={Thermostat} />;
}

// Hot-water zone — the cylinder/tap outline (app `HotWater`).
export function WaterIcon() {
  return <AppIcon glyph={HotWater} />;
}

// Holiday Mode action — the beach parasol.
export function HolidayIcon() {
  return <AppIcon glyph={Holiday} />;
}

// Data action — the bar chart with a trend line.
export function DataIcon() {
  return <AppIcon glyph={Data} />;
}

// Row / tile chevron. The app draws its left-pointing path turned 180° with a
// React Native transform style, which the react-native-svg shim renders as CSS.
export function ChevronRightIcon() {
  return <AppIcon glyph={ChevronRight} />;
}

// Heating status (calling for heat / on) — the flame.
export function FireIcon() {
  return <AppIcon glyph={Fire} />;
}

// Heating status (Off / frost protection / holiday) — the snowflake.
export function SnowflakeIcon() {
  return <AppIcon glyph={Snowflake} />;
}

// Schedule action — the calendar with an arrow.
export function ScheduleIcon() {
  return <AppIcon glyph={Schedule} />;
}

// Plus Hours / Boost action — the clock with a plus.
export function PlusHoursIcon() {
  return <AppIcon glyph={PlusHours} />;
}

// Geolocation tile — the location pin beside a house outline. The app draws it in
// two colours (the pin outline near-white, the pin's dot and the house in the pale
// brand green), so it keeps them rather than taking the container's one.
export function GeolocationIcon() {
  return <AppIcon glyph={Geolocation} tint={false} />;
}

// Header menu button — the three-bar burger.
export function MenuIcon() {
  return <AppIcon glyph={Menu} />;
}

// Dismiss control — the cross.
export function CloseIcon() {
  return <AppIcon glyph={Close} />;
}

// Help menu item — the circled "i".
export function InfoIcon() {
  return <AppIcon glyph={Info} />;
}

// Legal (the menu's Terms & Conditions / Privacy Notice section). The app draws
// this glyph on its performance-profiles screen rather than a legal one — it has
// no legal screen — but a balance is the plainest "legal" mark it owns, and
// borrowing it keeps the web from redrawing an icon.
export function ScaleIcon() {
  return <AppIcon glyph={Scale} />;
}

// Account (the menu's account settings) — the app's Menu draws its Account
// entry with this cog.
export function SettingsIcon() {
  return <AppIcon glyph={Settings} />;
}

// Help sections (the app's HelpMenu icons).
export function PlayCircleIcon() {
  return <AppIcon glyph={PlayCircle} />;
}
export function ClockOutlineIcon() {
  return <AppIcon glyph={ClockOutline} />;
}
export function DsrActiveIcon() {
  return <AppIcon glyph={DsrActive} />;
}
export function QuestionMarkCircleIcon() {
  return <AppIcon glyph={QuestionMarkCircle} />;
}

// Watch App topic list.
export function ThermometerIcon() {
  return <AppIcon glyph={Thermometer} />;
}
export function AutoModeIcon() {
  return <AppIcon glyph={AutoMode} />;
}

// A row that leaves for another site (app `ArrowTopRightOnSquare`).
export function ExternalLinkIcon() {
  return <AppIcon glyph={ArrowTopRightOnSquare} />;
}

// A password requirement that is met (the app's password rules); CloseIcon marks
// one that isn't.
export function TickIcon() {
  return <AppIcon glyph={Tick} />;
}

// A passkey, beside each in the Passkeys list: the app's (Material Symbols'
// "passkey"), the same for every passkey, whichever password manager holds it.
export function PasskeyIcon() {
  return <AppIcon glyph={Passkey} />;
}

// ---- Web-only glyphs ------------------------------------------------------------

function Svg({ viewBox, children }) {
  return (
    <svg viewBox={viewBox} width="24" height="24" focusable="false">
      {children}
    </svg>
  );
}

// An indeterminate loading spinner — a faint ring with a brighter leading arc. The
// spin is applied via the .spinner CSS class (and pauses under reduced-motion). Not
// wrapped in Svg: it is stroke-based and carries its own class.
export function SpinnerIcon() {
  return (
    <svg
      className="spinner"
      viewBox="0 0 24 24"
      width="24"
      height="24"
      fill="none"
      focusable="false"
    >
      <circle
        cx="12"
        cy="12"
        r="9"
        stroke="currentColor"
        strokeOpacity="0.25"
        strokeWidth="2.5"
      />
      <path
        d="M21 12a9 9 0 0 0-9-9"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

// Watch App help list — the map for the Navigation topic. The app takes this one
// from react-native-heroicons ("map", outline) rather than drawing it itself, so
// it is the heroicons path (MIT) in the same 24 box and 1.5 stroke.
export function MapIcon() {
  return (
    <Svg viewBox="0 0 24 24">
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 6.75V15m6-6v8.25m.503 3.498 4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 0 0-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0Z"
      />
    </Svg>
  );
}

// Passkeys (the Account page's passkey section) — a padlock. The app's Lock
// glyph fixes its own 20px size and black fill, so it can't be wrapped like the
// others; this is its path in the same 48 box, drawn at 24 in the current colour.
export function LockIcon() {
  return (
    <Svg viewBox="0 0 48 48">
      <path
        fill="currentColor"
        d="M11 44q-1.25 0-2.125-.875T8 41V19.3q0-1.25.875-2.125T11 16.3h3.5v-4.8q0-3.95 2.775-6.725Q20.05 2 24 2q3.95 0 6.725 2.775Q33.5 7.55 33.5 11.5v4.8H37q1.25 0 2.125.875T40 19.3V41q0 1.25-.875 2.125T37 44Zm0-3h26V19.3H11V41Zm13-7q1.6 0 2.725-1.1t1.125-2.65q0-1.5-1.125-2.725T24 26.3q-1.6 0-2.725 1.225T20.15 30.25q0 1.55 1.125 2.65Q22.4 34 24 34Zm-6.5-17.7h13v-4.8q0-2.7-1.9-4.6Q26.7 5 24 5q-2.7 0-4.6 1.9-1.9 1.9-1.9 4.6ZM11 41V19.3 41Z"
      />
    </Svg>
  );
}
